// MockupPreviewBanner.tsx
// Audit 2026-06-01 — mockup boundary hardening.
//
// Renders an unmistakable "Preview / Mockup Only — no account created"
// banner at the top of every Trading-UI screen that is intentionally NOT
// wired to a live backend. Each screen passes a unique `screen` prop
// so the rendered text + data-testid are deterministic for Playwright.
//
// Rules baked into the copy:
//   - "No real account is created."
//   - "No broker credentials are submitted."
//   - "No live orders are placed."
//
// This component is presentational only — zero side effects, no fetch.
import React from 'react';
import { AlertTriangle } from 'lucide-react';

export interface MockupPreviewBannerProps {
  /** Short identifier of the screen — used in data-testid + the visible label. */
  screen:
    | 'onboarding-mockup'
    | 'demo-onboarding-preview'
    | 'security-settings'
    | 'paper-api-connect'
    | 'admin-new-accounts';
}

const SCREEN_LABEL: Record<MockupPreviewBannerProps['screen'], string> = {
  'onboarding-mockup': 'Onboarding mockup',
  'demo-onboarding-preview': 'Demo onboarding preview',
  'security-settings': 'Security settings preview',
  'paper-api-connect': 'Paper API connection preview',
  'admin-new-accounts': 'Admin · New Accounts preview',
};

export function MockupPreviewBanner({ screen }: MockupPreviewBannerProps) {
  return (
    <div
      role="status"
      data-testid={`mockup-preview-banner-${screen}`}
      className="mb-4 flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-[12px] font-bold leading-relaxed text-amber-700 dark:text-amber-300"
    >
      <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
      <div>
        <div className="text-[11px] font-black uppercase tracking-widest">
          Preview / Mockup Only
        </div>
        <div data-testid={`mockup-preview-banner-${screen}-body`}>
          {SCREEN_LABEL[screen]}. No real account is created. No broker
          credentials are submitted. No live orders are placed.
        </div>
      </div>
    </div>
  );
}

export default MockupPreviewBanner;
